import { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Premium, Hero-consistent language selector. Controls the WEBSITE/interface language only —
// never business country, payroll jurisdiction or currency. Works for logged-out visitors.
export function LanguageSelector({ align = "right" }) {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const list = languages && languages.length ? languages : [{ code: "en", name: "English" }];
  const current = list.find((l) => l.code === lang) || { code: "en", name: "English" };
  const [flash, setFlash] = useState(false);

  const choose = (code) => {
    setFlash(true);
    setTimeout(() => setFlash(false), 450); // immediate visual acknowledgement
    setLang(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="hero-language-selector" data-no-translate>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="hero-language-button"
        aria-haspopup="listbox" aria-expanded={open}
        className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm border backdrop-blur-md transition-all duration-300 ease-out ${flash ? "text-[#D4AF37] bg-[#D4AF37]/12 border-[#D4AF37]/40 scale-[1.03]" : "bg-[#101013]/60 border-white/12 text-white/85 hover:text-[#D4AF37] hover:border-[#D4AF37]/40"}`}>
        <Globe className={`w-4 h-4 shrink-0 ${flash ? "animate-spin" : ""}`} style={flash ? { animationDuration: "0.6s" } : undefined} />
        <span className="font-medium tracking-[0.02em] hidden xs:inline sm:inline">{current.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="hero-language-menu" role="listbox"
          className={`absolute z-[80] mt-2 w-52 max-h-[60vh] overflow-y-auto rounded-2xl border border-white/12 bg-[#0E0E11] shadow-[0_24px_60px_-18px_rgba(0,0,0,0.95)] ring-1 ring-black/40 p-1.5 ${align === "right" ? "right-0" : "left-0"}`}>
          {list.map((l) => {
            const active = l.code === lang;
            return (
              <button key={l.code} type="button" role="option" aria-selected={active}
                data-testid={`hero-language-option-${l.code}`}
                onClick={() => choose(l.code)}
                className={`w-full flex items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors duration-200 ${active ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-white/75 hover:bg-white/[0.05] hover:text-white"}`}>
                <span className="truncate">{l.name}{l.rtl ? " · RTL" : ""}</span>
                {active && <Check className="w-4 h-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
